import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { InformationCircleIcon, XIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { FocusEvent, ReactElement, useEffect, useRef, useState } from 'react';

import { usePartnerTheming } from '~/hooks/usePartnerTheming';

type TooltipProps = {
  children: ReactElement;
  title: string;
};

export const Tooltip = ({ children, title }: TooltipProps) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const {
    tooltip: { borderRadius, border, tone, closeIconPresent, css: tooltipCss },
  } = usePartnerTheming();

  const onCloseTooltip = () => {
    setIsTooltipVisible(false);
    triggerRef.current?.focus();
  };

  /** Focus on Tooltip if it's present */
  useEffect(() => {
    if (tooltipRef && isTooltipVisible) {
      tooltipRef.current?.focus();
    }
  }, [isTooltipVisible]);

  /** Focus back on button after closing tooltip */
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseTooltip();
      }
    };

    if (isTooltipVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTooltipVisible]);

  const handleOutsideClick = (event: MouseEvent) => {
    if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
      onCloseTooltip();
    }
  };

  useEffect(() => {
    if (isTooltipVisible) {
      document.addEventListener('click', handleOutsideClick);
    } else {
      document.removeEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isTooltipVisible]);

  return (
    <Stack gap="large" position="relative">
      <Box
        cursor="pointer"
        aria-haspopup="dialog"
        aria-expanded={isTooltipVisible}
        as="button"
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="xsmall"
        onClick={() => setIsTooltipVisible(!isTooltipVisible)}
      >
        <Text
          size="xsmall"
          css={css({
            textDecoration: 'underline',
            textDecorationStyle: 'dashed',
            textUnderlineOffset: 3,
            '&:hover': {
              textDecoration: 'none',
            },
          })}
        >
          {title}
        </Text>
        <InformationCircleIcon size="xsmall" />
      </Box>
      {isTooltipVisible ? (
        <Stack
          ref={tooltipRef}
          role="dialog"
          position="absolute"
          zIndex="notification"
          padding="large"
          gap="large"
          border={border}
          borderRadius={borderRadius}
          css={css(tooltipCss)}
          onBlur={(event: FocusEvent<HTMLDivElement, Element>) => {
            if (!tooltipRef.current?.contains(event.relatedTarget)) {
              onCloseTooltip();
            }
          }}
        >
          {closeIconPresent ? (
            <Box as="button" alignSelf="end" onClick={onCloseTooltip} cursor="pointer">
              <XIcon size="xsmall" tone={tone} />
            </Box>
          ) : null}
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
};
